/**
 * FAQEditor.tsx — FAQ 问答编辑弹窗
 *
 * 1:1 移植自原型 design/Novel-admin/src/dialogs2.jsx 421–485（FAQEditor）。
 * 按语言拆行：本行 question_i18n / answer_i18n 有且仅有当前语言一个 key（dev-docs
 * `01-通用约定/85-运营配置维度模型.md` §5 + DB CHECK `is_valid_scoped_title_i18n`），
 * 缺该语言 = 该语言无配置，**不回退英语**。故只录当前语言一行、不开「其他语言」。
 * 机械转换：JS→TS；window.__multiLang.useMultiLang/MultiLangFormRows（非受控）→
 *   本地 useState + 受控 ~/ui/MultiLangFormRows（答案 type==='textarea' → multiline）；
 *   原型 DialogShell → ~/ui/Dialog；FormRow / Switch 收口到共享 ~/ui；Select 就近内联。
 * 视觉层（JSX/className/inline-style）与原型逐字一致。
 *
 * 接真（#221 FAQ canonical）：「保存草稿」/「保存并上架」→ REST CRUD（root/admin/operator）：
 *   - 新建 → createFaq（POST /api/v1/ops/faqs，app_id=null 全局层 + slug + category_key）；
 *   - 编辑 → updateFaq（PATCH /api/v1/ops/faqs/:id，仅 category_key / 问答 / 排序 / is_active，slug·app_id 不可改）。
 *   - category_key 从 useFaqCategories() live 分类候选选（提交回传稳定键，非展示名）；
 *   - question_i18n / answer_i18n **当前语言单 key 必填非空**；display_order 提交；
 *     is_active 由按钮区分（草稿 false / 上架 true）；
 *   - 新建必填 slug（kebab-case，上线后不可改）；编辑不传 slug（PATCH 不含，service 保留既有）。
 *   - 成功 → toast + invalidate ['faqs']（mutation 内）；失败 → 错误 toast 不假成功、表单不丢。
 *   - save 受 canUpsert gate（adversarial F3）：viewer / 会话内降级 → 两个 save 按钮 disabled + submit 早退，防发被拒写。
 */

import {
  type CreateFaqRequest,
  LEGAL_PAGE_PATHS,
  type UpdateFaqRequest,
  findNonCanonicalLegalLinks,
} from '@novel-admin/shared';
import { useState } from 'react';
import { buildFaqCategoryOptions } from '~/data/adapters/ops-adapter';
import { type Faq, useFaqCategories } from '~/data/queries';
import { Dialog } from '~/ui/Dialog';
import { type FormRowProps, FormRow as UIFormRow } from '~/ui/FormRow';
import { MultiLangFormRows } from '~/ui/MultiLangFormRows';
import { Select } from '~/ui/Select';
import { Switch } from '~/ui/Switch';
import { isOpsGlobal } from '../ops-scope';
import { type OpsMutations, errorToast, successToast } from '../use-ops-mutations';

// ops 域弹窗内表单行：复用共享 ~/ui/FormRow，固定 dialogs 历史尺寸（labelWidth140 / gap14 / pad / labelPadTop6）
const FormRow = (p: FormRowProps) => (
  <UIFormRow labelWidth={140} gap={14} pad="12px 0" labelPadTop={6} {...p} />
);

const SLUG_RE = /^[a-z0-9][a-z0-9-]*$/;

export function FAQEditor({
  faq,
  mutations,
  canUpsert,
  appId,
  lang,
  onClose,
}: {
  faq?: Faq;
  mutations: OpsMutations;
  /** 写权（root/admin/operator）；viewer=false → save 禁用（adversarial F3：会话内降级也随之失能，防发被拒 PUT）*/
  canUpsert: boolean;
  appId: string;
  lang: string;
  onClose: () => void;
}) {
  const isEdit = !!faq;
  // 分类下拉候选 = useFaqCategories() live 分类（#221 可配置注册表，取代旧闭集 enum）；提交回传 value=category_key。
  // 只列 is_active 分类（下架分类不可新选）；但编辑既有 FAQ 时若其 categoryKey 指向已下架分类，仍纳入当前项——
  // 否则该 FAQ 的分类值渲染空白 / 不 round-trip（保存会静默改分类）。
  const { data: faqCats } = useFaqCategories();
  const activeCats = faqCats.filter((c) => c.isActive);
  const currentInactive =
    isEdit && faq && !activeCats.some((c) => c.key === faq.categoryKey)
      ? faqCats.filter((c) => c.key === faq.categoryKey)
      : [];
  const categoryOptions = buildFaqCategoryOptions([...activeCats, ...currentInactive]);
  // 选中分类：编辑回填既有 categoryKey；新建默认首个 live 分类（分类未就绪时 '' → effectiveCategory 兜底首项/OTHER）。
  const [category, setCategory] = useState<string>(faq?.categoryKey ?? '');
  // effectiveCategory：分类异步加载完成后（首项晚到）令 Select 落到首个可选分类，避免停留空选无法提交。
  const effectiveCategory = category || categoryOptions[0]?.value || 'OTHER';
  const [slug, setSlug] = useState('');
  const [displayOrder, setDisplayOrder] = useState(String(faq?.sort ?? 1));
  const [question, setQuestion] = useState<Record<string, string>>(() =>
    faq?.questionI18n ? { ...faq.questionI18n } : { [lang]: '' },
  );
  const [answer, setAnswer] = useState<Record<string, string>>(() =>
    faq?.answerI18n ? { ...faq.answerI18n } : { [lang]: '' },
  );

  // 按语言分行后本行只有当前语言一个 key（与 TitleLangsDialog 同口径）。
  const i18nValid = (m: Record<string, string>) => !!m[lang]?.trim();
  const slugValid = isEdit || SLUG_RE.test(slug);
  const formValid = i18nValid(question) && i18nValid(answer) && slugValid;
  // dev-docs 60 §8：法务外链必须走固定路径（曾有 FAQ 外链用四个自造路径、线上全 404）。
  // 只提示不阻断：答案是自由正文，可能合法地引用第三方条款。
  const nonCanonicalLegalLinks = findNonCanonicalLegalLinks(answer[lang] ?? '');
  const busy = mutations.createFaq.isPending || mutations.updateFaq.isPending;

  const submit = (isActive: boolean) => {
    // canUpsert gate（adversarial F3）：表单有效性之外再加写权门禁，viewer/降级会话点不动也提交不了。
    if (!canUpsert || !formValid) return;
    // 本行只提交当前语言这一个 key：多 key 会被 DB CHECK 拒（§5），录了也无处可存。
    const question_i18n = { [lang]: question[lang]?.trim() ?? '' };
    const answer_i18n = { [lang]: answer[lang]?.trim() ?? '' };
    const display_order = Number(displayOrder) || 0;
    const finish = () => {
      successToast(isActive ? '已保存并上架该 FAQ' : '已保存为草稿（下架）');
      onClose();
    };
    if (isEdit) {
      // 编辑：PATCH sparse——slug / app_id / is_seed_baseline 不可改故不传；仅 category_key / 问答 / 排序 / 上下架。
      const body: UpdateFaqRequest = {
        category_key: effectiveCategory,
        question_i18n,
        answer_i18n,
        display_order,
        is_active: isActive,
      };
      mutations.updateFaq.mutate(
        { id: faq.id, body },
        { onSuccess: finish, onError: (err) => errorToast(err, '保存 FAQ 失败') },
      );
      return;
    }
    // 新建：POST 不带 is_active——服务端硬编码 is_active=false（「先建后审再上架」治理流，§9.2）。
    // 「保存并上架」= 先建（草稿）→ 成功后 PATCH 上架（create-then-activate）；「保存草稿」= 仅建。
    const body: CreateFaqRequest = {
      app_id: isOpsGlobal(appId) ? null : appId,
      language: lang,
      slug,
      category_key: effectiveCategory,
      question_i18n,
      answer_i18n,
      display_order,
    };
    mutations.createFaq.mutate(body, {
      onSuccess: (created) => {
        if (!isActive) {
          finish();
          return;
        }
        mutations.updateFaq.mutate(
          { id: created.id, body: { is_active: true } },
          { onSuccess: finish, onError: (err) => errorToast(err, '已创建，上架失败') },
        );
      },
      onError: (err) => errorToast(err, '创建 FAQ 失败'),
    });
  };

  const submitTitle = !canUpsert
    ? '权限不足 · 该操作受角色限制（后端 requireRole 为权威）'
    : !formValid
      ? !slugValid
        ? 'slug 须为 kebab-case（小写字母/数字/连字符，不以连字符开头）'
        : `问题 / 答案须填写当前语言 ${lang.toUpperCase()}`
      : undefined;

  return (
    <Dialog
      width={680}
      title={faq ? `编辑 FAQ · ${faq.id}` : '新建 FAQ'}
      subtitle={`${isOpsGlobal(appId) ? '该语言全局' : 'App 独立配置'} · 只维护 ${lang.toUpperCase()} 单 key`}
      icon="IconInfo"
      onClose={onClose}
      footer={
        <>
          <div style={{ flex: 1, fontSize: 11, color: 'var(--fg-muted)' }}>
            其他语言请切到该语言芯片单独维护 · 该语言无配置时 C 端不展示，不回退英文
          </div>
          <button className="btn" onClick={onClose}>
            取消
          </button>
          <button
            className="btn"
            disabled={!canUpsert || !formValid || busy}
            title={submitTitle}
            onClick={() => submit(false)}
          >
            保存草稿
          </button>
          <button
            className="btn btn-accent"
            disabled={!canUpsert || !formValid || busy}
            title={submitTitle}
            onClick={() => submit(true)}
          >
            保存并上架
          </button>
        </>
      }
    >
      <div style={{ padding: 18 }}>
        <FormRow label="分类" required>
          <Select
            className="input"
            value={effectiveCategory}
            onChange={(e) => setCategory(e.target.value)}
            style={{ maxWidth: 280 }}
          >
            {categoryOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </FormRow>
        {!isEdit && (
          <FormRow label="深链 slug" required hint="kebab-case · 上线后不可改">
            <input
              className="input mono"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="如：coin-not-arrived"
              style={{ maxWidth: 280 }}
            />
          </FormRow>
        )}
        <FormRow label="排序">
          <input
            className="input mono"
            value={displayOrder}
            onChange={(e) => setDisplayOrder(e.target.value)}
            style={{ width: 80, textAlign: 'right' }}
          />
        </FormRow>
        <FormRow
          label="上架状态"
          hint={
            isEdit
              ? '用底部「保存草稿 / 保存并上架」决定'
              : '新建默认下架（草稿）；点「保存并上架」= 创建后立即上架'
          }
        >
          <Switch on={faq?.isActive ?? false} size="sm" disabled />
        </FormRow>

        {/* 问题 · 当前语言单 key */}
        <div style={{ padding: '16px 0 4px', borderBottom: '1px dashed var(--border)' }}>
          <span style={{ fontWeight: 500, fontSize: 12.5 }}>问题</span>
          <span style={{ fontSize: 11, color: 'var(--fg-muted)', marginLeft: 6 }}>
            · 客户端展示的提问标题
          </span>
        </div>
        <MultiLangFormRows
          label="问题"
          value={question}
          onChange={setQuestion}
          requiredLangs={[lang]}
          showOtherLangs={false}
          placeholderMap={{
            zh: '为什么充值后 COIN 没有到账？',
            en: "Why didn't my COINs arrive after payment?",
          }}
        />

        {/* 答案 · 当前语言单 key */}
        <div style={{ padding: '16px 0 4px', borderBottom: '1px dashed var(--border)' }}>
          <span style={{ fontWeight: 500, fontSize: 12.5 }}>答案</span>
          <span style={{ fontSize: 11, color: 'var(--fg-muted)', marginLeft: 6 }}>
            · 支持多段文字
          </span>
        </div>
        <MultiLangFormRows
          label="答案"
          value={answer}
          onChange={setAnswer}
          multiline
          requiredLangs={[lang]}
          showOtherLangs={false}
          placeholderMap={{ zh: '答案 (中文)…', en: 'Answer (English)…' }}
        />
        {nonCanonicalLegalLinks.length > 0 && (
          <div
            className="card-flat"
            role="note"
            style={{
              marginTop: 8,
              padding: '6px 10px',
              fontSize: 11.5,
              background: 'var(--warn-soft)',
            }}
          >
            答案里有法务类外链不在固定路径上（{nonCanonicalLegalLinks.join('、')}）。若指向本 App
            的法务页， 路径只能是品牌域名根下的 {Object.values(LEGAL_PAGE_PATHS).join(' / ')}
            ，自造路径线上会 404； 引用第三方条款可忽略本提示。不阻断保存。
          </div>
        )}
      </div>
    </Dialog>
  );
}
